import {
  type ExamRoomRequestPhysicalAttributes,
  type FeatureCheck,
  useGetDownloadSignedUrlForExamRoomRequestPhysicalInfoLazyQuery,
} from '@exam-center/graphql';
import { Box, Divider, Flex, Text } from '@shared/components';
import { useEffect, useMemo, useState } from 'react';

import { PHYSICAL_FEATURES, AROUND_AREA_FEATURES } from '..';
import { ReadonlyPhysicalInfoFeatureCheckRow } from '../../components/exam-room-info-details';
import ReadonlyDoorInfoRow from '../../components/exam-room-info-details/readonly-door-info-row';
import { ReadonlyFormImagesInput } from '../../components/readonly-form-images-input';
import { useAdminExamRoomRequestInfo } from '../../providers';

export default function AdminExamRoomRequestPhysicalInfoDetails() {
  const { examRoomRequestInfo } = useAdminExamRoomRequestInfo();

  const info = examRoomRequestInfo.examRoomRequestPhysicalInfo?.info as
    | ExamRoomRequestPhysicalAttributes
    | null
    | undefined;

  const [getDownloadSignedUrls] = useGetDownloadSignedUrlForExamRoomRequestPhysicalInfoLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const [signedUrlMap, setSignedUrlMap] = useState<Record<string, string>>({});

  const fileNames = useMemo(() => {
    const names: string[] = [];
    if (!info) return names;

    PHYSICAL_FEATURES.filter((f) => f.hasImages).forEach((f) => {
      const feature = info[f.key as keyof ExamRoomRequestPhysicalAttributes] as
        | FeatureCheck
        | null
        | undefined;
      const imgs = feature?.images;
      if (Array.isArray(imgs)) names.push(...imgs);
    });

    AROUND_AREA_FEATURES.filter((f) => f.hasImages).forEach((f) => {
      const feature = info[f.key as keyof ExamRoomRequestPhysicalAttributes] as
        | FeatureCheck
        | null
        | undefined;
      const imgs = feature?.images;
      if (Array.isArray(imgs)) names.push(...imgs);
    });

    if (Array.isArray(info.entryExitDoors?.images))
      names.push(...(info.entryExitDoors?.images as string[]));
    if (Array.isArray(info.frontOfExamRoom?.images))
      names.push(...(info.frontOfExamRoom?.images as string[]));
    if (Array.isArray(info.insideExamRoom?.images))
      names.push(...(info.insideExamRoom?.images as string[]));

    return Array.from(new Set(names)).filter(Boolean) as string[];
  }, [info]);

  useEffect(() => {
    if (!fileNames.length) return;

    (async () => {
      const result = await getDownloadSignedUrls({
        variables: {
          getDownloadSignedUrlForExamRoomRequestPhysicalInfoInput: {
            fileNames,
            examRoomRequestInfoId: examRoomRequestInfo.id,
          },
        },
        fetchPolicy: 'no-cache',
      });

      const map = (result.data?.ecGetDownloadSignedUrlForExamRoomRequestPhysicalInfo?.signedUrls ??
        {}) as Record<string, string>;
      setSignedUrlMap(map);
    })();
  }, [fileNames, getDownloadSignedUrls, examRoomRequestInfo.id]);

  useEffect(() => {
    const frameContent = document.getElementById('frame-content');
    if (frameContent) {
      frameContent.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  const resolvedInfo = useMemo(() => {
    if (!info) return info;
    const resolve = (names?: string[] | null) =>
      (names ?? []).map((n) => (n ? signedUrlMap[n] : '')).filter((u): u is string => Boolean(u));

    return {
      ...info,
      securityCameras: info.securityCameras
        ? { ...info.securityCameras, images: resolve(info.securityCameras.images) }
        : null,
      registrationArea: info.registrationArea
        ? { ...info.registrationArea, images: resolve(info.registrationArea.images) }
        : null,
      entryExitDoors: info.entryExitDoors
        ? { ...info.entryExitDoors, images: resolve(info.entryExitDoors.images) }
        : null,
      frontOfExamRoom: info.frontOfExamRoom
        ? { ...info.frontOfExamRoom, images: resolve(info.frontOfExamRoom.images) }
        : null,
      insideExamRoom: info.insideExamRoom
        ? { ...info.insideExamRoom, images: resolve(info.insideExamRoom.images) }
        : null,
    } as ExamRoomRequestPhysicalAttributes;
  }, [info, signedUrlMap]);

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(8),
        padding: theme.spacing(8),
      })}
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          gap: theme.spacing(6),
          [theme.breakpoints.down('desktop')]: {
            flexDirection: 'column',
          },
        })}
      >
        <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
          ลักษณะทางกายภาพ
        </Text>
        <Box
          sx={(theme) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          {PHYSICAL_FEATURES.map((f) => {
            const feature = resolvedInfo?.[f.key as keyof ExamRoomRequestPhysicalAttributes] as
              | FeatureCheck
              | null
              | undefined;
            return (
              <ReadonlyPhysicalInfoFeatureCheckRow
                key={f.key}
                label={f.label}
                required={!!f.required}
                value={feature?.valid ?? null}
                additionalInfo={feature?.additionalInfo ?? ''}
                hasImages={!!f.hasImages}
                images={feature?.images ?? []}
                isReadonly={true}
                imageHelperText={f.imageInputLabel}
              />
            );
          })}
          <ReadonlyDoorInfoRow
            label='8.จำนวนประตูเปิดเข้า-ออกห้องสอบ'
            numberOfDoors={info?.entryExitDoors?.numberOfDoors ?? undefined}
            images={resolvedInfo?.entryExitDoors?.images ?? []}
          />
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              [theme.breakpoints.down('desktop')]: {
                flexDirection: 'column',
              },
            })}
          >
            <Box sx={{ minWidth: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                9.หน้าห้องสอบ
              </Text>
            </Box>
            <ReadonlyFormImagesInput
              images={resolvedInfo?.frontOfExamRoom?.images ?? []}
              label='แนบรูปภาพบริเวณหน้าห้องสอบ'
            />
          </Flex>
          <Flex
            sx={(theme) => ({
              gap: theme.spacing(6),
              justifyContent: 'space-between',
              [theme.breakpoints.down('desktop')]: {
                flexDirection: 'column',
              },
            })}
          >
            <Box sx={{ minWidth: '320px' }}>
              <Text variant='body1' sx={{ fontWeight: 500 }}>
                10.ภายในห้องสอบเป็นภาพกว้าง
              </Text>
            </Box>
            <ReadonlyFormImagesInput
              images={resolvedInfo?.insideExamRoom?.images ?? []}
              label='แนบรูปภาพบริเวณภายในห้องสอบ'
            />
          </Flex>
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'row',
          gap: theme.spacing(6),
          [theme.breakpoints.down('desktop')]: {
            flexDirection: 'column',
          },
        })}
      >
        <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
          ลักษณะบริเวณโดยรอบของห้องสอบ
        </Text>
        <Box
          sx={(theme) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(6),
          })}
        >
          {AROUND_AREA_FEATURES.map((f) => {
            const feature = resolvedInfo?.[f.key as keyof ExamRoomRequestPhysicalAttributes] as
              | FeatureCheck
              | null
              | undefined;
            return (
              <ReadonlyPhysicalInfoFeatureCheckRow
                key={f.key}
                label={f.label}
                required={!!f.required}
                value={feature?.valid ?? null}
                additionalInfo={feature?.additionalInfo ?? ''}
                hasImages={!!f.hasImages}
                images={feature?.images ?? []}
                isReadonly={true}
                imageHelperText={f.imageInputLabel}
              />
            );
          })}
        </Box>
      </Box>
    </Flex>
  );
}
